/**
 * Mode rafale (PDA) : chaque scan ajoute 1 au stock, sans toucher l'écran.
 * Modèle pur : la session (articles, +n), la file des écritures (une clé par scan, envoyées
 * une à une, renvoyées telles quelles après une coupure) et les codes inconnus à traiter.
 */
import type { StockModeProduct } from '@/api/stocks/inventory/stockModeApi';
import type { AppError } from '@/api/errors';

export type BurstKind = 'restock' | 'adjustment';

export type BurstOp = {
  /** Clé du scan : le serveur n'applique qu'une fois la même clé. */
  key: string;
  productId: string;
  delta: number;
  kind: BurstKind;
  note?: string;
};

export type BurstLine = {
  product: StockModeProduct;
  /** Reçu pendant la session, corrections comprises (écritures en attente incluses). */
  received: number;
  /** Stock rendu par la dernière écriture confirmée, null avant la première. */
  confirmedStock: number | null;
};

export type BurstState = {
  tenantId: string | null;
  lines: BurstLine[];
  queue: BurstOp[];
  unknown: string[];
};

export const emptyBurst = (tenantId: string | null): BurstState => ({
  tenantId,
  lines: [],
  queue: [],
  unknown: [],
});

const MAX_LINES = 100;
const MAX_UNKNOWN = 50;

const upsertLine = (lines: BurstLine[], product: StockModeProduct, delta: number): BurstLine[] => {
  const previous = lines.find((line) => line.product.id === product.id);
  const next: BurstLine = {
    product,
    received: (previous?.received ?? 0) + delta,
    confirmedStock: previous?.confirmedStock ?? null,
  };
  return [next, ...lines.filter((line) => line.product.id !== product.id)].slice(0, MAX_LINES);
};

/** Scan reconnu : +1 tout de suite à l'écran, l'écriture part dans la file. */
export const addScan = (state: BurstState, product: StockModeProduct, key: string): BurstState => ({
  ...state,
  lines: upsertLine(state.lines, product, 1),
  queue: [...state.queue, { key, productId: product.id, delta: 1, kind: 'restock' }],
});

/** « − » sur une ligne : correction tracée. */
export const addCorrection = (
  state: BurstState,
  product: StockModeProduct,
  delta: number,
  key: string,
): BurstState => {
  const line = state.lines.find((item) => item.product.id === product.id);
  return {
    ...state,
    lines: line
      ? state.lines.map((item) =>
          item.product.id === product.id ? { ...item, received: item.received + delta } : item,
        )
      : state.lines,
    queue: [
      ...state.queue,
      { key, productId: product.id, delta, kind: 'adjustment', note: 'Correction (rafale)' },
    ],
  };
};

/**
 * « Annuler la rafale » : pour chaque article, la quantité reçue est retirée par un mouvement
 * de correction, envoyé après les scans encore en file. La session repart de zéro.
 */
export const cancelBurst = (state: BurstState, newKey: () => string): BurstState => ({
  ...state,
  lines: [],
  queue: [
    ...state.queue,
    ...state.lines
      .filter((line) => line.received !== 0)
      .map<BurstOp>((line) => ({
        key: newKey(),
        productId: line.product.id,
        delta: -line.received,
        kind: 'adjustment',
        note: 'Annulation de la rafale',
      })),
  ],
});

/** Écriture acceptée : sortie de la file, stock du serveur retenu. */
export const confirmOp = (state: BurstState, key: string, stock: number): BurstState => {
  const op = state.queue.find((item) => item.key === key);
  if (!op) return state;
  return {
    ...state,
    queue: state.queue.filter((item) => item.key !== key),
    lines: state.lines.map((line) =>
      line.product.id === op.productId ? { ...line, confirmedStock: stock } : line,
    ),
  };
};

/** Écriture refusée pour de bon (article supprimé…) : retirée et décomptée. */
export const dropOp = (state: BurstState, key: string): BurstState => {
  const op = state.queue.find((item) => item.key === key);
  if (!op) return state;
  return {
    ...state,
    queue: state.queue.filter((item) => item.key !== key),
    lines: state.lines.map((line) =>
      line.product.id === op.productId ? { ...line, received: line.received - op.delta } : line,
    ),
  };
};

export const addUnknown = (state: BurstState, code: string): BurstState => ({
  ...state,
  unknown: [code, ...state.unknown.filter((item) => item !== code)].slice(0, MAX_UNKNOWN),
});

export const removeUnknown = (state: BurstState, code: string): BurstState => ({
  ...state,
  unknown: state.unknown.filter((item) => item !== code),
});

/** Stock affiché : dernier stock confirmé plus les écritures encore en file. */
export const displayStock = (line: BurstLine, queue: readonly BurstOp[]): number => {
  const pending = queue
    .filter((op) => op.productId === line.product.id)
    .reduce((sum, op) => sum + op.delta, 0);
  const base =
    line.confirmedStock !== null ? line.confirmedStock : line.product.stock + line.received;
  return Math.max(0, line.confirmedStock !== null ? base + pending : base);
};

/** Coupure réseau ou session à rafraîchir : on renverra la même écriture plus tard. */
export const isTransientError = (error: AppError): boolean => {
  switch (error.type) {
    case 'network':
    case 'unknown':
      return true;
    case 'auth':
      return error.reason === 'expired';
    case 'supabase':
      return !error.code;
    default:
      return false;
  }
};

/** Relu sur l'appareil : seulement la session du même commerce, sinon une session vide. */
export const restoreBurst = (raw: unknown, tenantId: string | null): BurstState => {
  if (!raw || typeof raw !== 'object') return emptyBurst(tenantId);
  const saved = raw as Partial<BurstState>;
  if (!tenantId || saved.tenantId !== tenantId) return emptyBurst(tenantId);
  return {
    tenantId,
    lines: Array.isArray(saved.lines) ? saved.lines : [],
    queue: Array.isArray(saved.queue) ? saved.queue : [],
    unknown: Array.isArray(saved.unknown) ? saved.unknown : [],
  };
};
